import React, { Suspense, createContext, lazy, useCallback, useContext, useMemo, useState } from 'react';
import TimeEventLoading from '../Timevents/TimeEventLoadingSimple';

const PDFViewer = lazy(() => import('./PDFViewer'));
const ImageViewer = lazy(() => import('./ImageViewer'));
const OtherViewer = lazy(() => import('./OtherViewer'));
const AudioViewer = lazy(() => import('./AudioViewer'));
const ExcelViewer = lazy(() => import('./ExcelViewer'));
const HTMLViewer = lazy(() => import('./HTMLViewer'));
const TextViewer = lazy(() => import('./TextViewer'));
const VideoViewer = lazy(() => import('./VideoViewer'));
const EmailViewer = lazy(() => import('./EmailViewer'));
const ZipViewer = lazy(() => import('./ZipViewer'));
const WordViewer = lazy(() => import('./WordViewer'));
const PowerPointViewer = lazy(() => import('./PowerPointViewer'));
const CSVViewer = lazy(() => import('./CSVViewer'));
const JSONViewer = lazy(() => import('./JSONViewer'));

const VIEWER_COMPONENTS = {
  pdf: PDFViewer,
  image: ImageViewer,
  other: OtherViewer,
  audio: AudioViewer,
  excel: ExcelViewer,
  html: HTMLViewer,
  text: TextViewer,
  video: VideoViewer,
  email: EmailViewer,
  zip: ZipViewer,
  word: WordViewer,
  powerpoint: PowerPointViewer,
  csv: CSVViewer,
  json: JSONViewer,
};

const FileViewerContext = createContext({
  openFileViewer: () => {},
  closeFileViewer: () => {},
});

export const useFileViewer = () => useContext(FileViewerContext);

const GlobalFileViewer = React.memo(({ viewer, onClose }) => {
  if (!viewer?.type) {
    return null;
  }

  const ViewerComponent = VIEWER_COMPONENTS[viewer.type];
  if (!ViewerComponent) {
    return null;
  }

  return (
    <Suspense fallback={<TimeEventLoading />}>
      <ViewerComponent
        open
        onClose={onClose}
        filename={viewer.filename || ''}
        eventId={viewer.eventId ?? null}
        filePath={viewer.filePath || ''}
        timeevent={viewer.timeevent || null}
      />
    </Suspense>
  );
});

const FileViewerProvider = ({ children }) => {
  const [activeViewer, setActiveViewer] = useState(null);

  const closeFileViewer = useCallback(() => {
    setActiveViewer(null);
  }, []);

  const openFileViewer = useCallback((viewer) => {
    setActiveViewer(viewer);
  }, []);

  const value = useMemo(() => ({
    openFileViewer,
    closeFileViewer,
  }), [openFileViewer, closeFileViewer]);

  return (
    <FileViewerContext.Provider value={value}>
      {children}
      <GlobalFileViewer viewer={activeViewer} onClose={closeFileViewer} />
    </FileViewerContext.Provider>
  );
};

export default FileViewerProvider;
